import { h, Component, Fragment } from "preact";
import moment from 'moment';

export let categories_map = ["WC.FD", "C.FD", "FD", "C.HD", "HD", "WC.HD"]

// PTO World Ranking threshold for WRS Auto qualification (start/wait list logic)
export const PTO_TOP_RANK_LIMIT = 40;

// wildcard_order 1-5 is a single sequential numbering across the two tentative wait-list
// sections: 1-2 fill the WAIT LIST (TENTATIVE STARTERS) wildcard slots, 3-5 fill the
// WAIT LIST (REMAINING ATHLETES) wildcard slots.
export const TENTATIVE_STARTER_WILDCARD_ORDERS = [1, 2];
export const REMAINING_ATHLETE_WILDCARD_ORDERS = [3, 4, 5];
export const ALL_WILDCARD_ORDERS = [...TENTATIVE_STARTER_WILDCARD_ORDERS, ...REMAINING_ATHLETE_WILDCARD_ORDERS];

/**
 * Returns the appropriate explainer JSX for a participant list.
 *
 * @param {'startList'|'waitList'} listType  — which list is being rendered
 * @param {Object} options
 * @param {boolean} options.isTentativeList     — wait-list-derived tentative starters (no official start list yet)
 * @param {boolean} options.isGrandFinal        — Grand Final race
 * @param {boolean} options.shouldHideTentative  — hard-coded race IDs that suppress tentative text
 * @param {number}  options.cutoffDays          — pre-race cutoff window (default 33)
 * @returns {preact.VNode|null}
 */
export function getParticipantExplainer(listType, { isTentativeList = false, isGrandFinal = false, shouldHideTentative = false, cutoffDays = 33 } = {}) {
	// ── Wait list ────────────────────────────────────────────────────
	if (listType === 'waitList') {
		if (isGrandFinal) {
			return (
				<p className="participants-explainer">
					Only athletes with a standing position in the current season's T100 Triathlon
					World Tour Standings are eligible. These athletes are sorted in wait-list order
					with the athlete at the top of the list next to receive an invitation to race
					should a spot open up (if the field size drops below 22 athletes).<br/><br/>
					The athletes below have been deemed eligible to race by their National Triathlon
					Federations. Athletes granted eligibility after the initial wait list cut-off
					date are 'late entries' and fall to the back of the queue for consideration
					regardless of their T100 Standings position.
				</p>
			)
		}

		return (
			<p className="participants-explainer">
				These athletes are sorted in wait-list order with the athlete at the top of the list
				next to receive an invitation to race should a spot open up.<br/><br/>
				3 spots here are reserved for discretionary Wildcard athletes. A spot shows the
				assigned Wildcard athlete once confirmed, or "WILDCARD SPOT" if not yet
				assigned.<br/><br/>
				The athletes below have been deemed eligible to race by their National Triathlon
				Federations. Athletes granted eligibility after the initial wait list cut-off date are 'late
				entries' and fall to the back of the queue for consideration regardless of their PTO World
				Ranking position.
			</p>
		)
	}

	// ── Start list ───────────────────────────────────────────────────

	// Grand Final — tentative
	if (isGrandFinal && isTentativeList) {
		return (
			<p className="participants-explainer">
				This is not an official start list: these athletes are on the wait list and are the
				likely starters for the T100 Grand Final.<br/><br/>
				Inclusion criteria for the T100 Triathlon World Championship Final Start List is:
				the top 24-ranked athletes in the current season's T100 Triathlon World Tour
				Standings. If there is another T100 event between the initial publication of the
				start list and the T100 Triathlon World Championship Final, any athletes who move
				into the top 24 of the T100 Standings will also be eligible to race.<br/><br/>
				All athletes have been deemed eligible to race by their National Triathlon Federations
				and are sorted here in T100 Standings order.
			</p>
		)
	}

	// Grand Final — official
	if (isGrandFinal && !isTentativeList) {
		return (
			<p className="participants-explainer">
				Inclusion criteria for the T100 Triathlon World Championship Final Start List is:
				the top 24-ranked athletes in the current season's T100 Triathlon World Tour
				Standings. If there is another T100 event between the initial publication of the
				start list and the T100 Triathlon World Championship Final, any athletes who move
				into the top 24 of the T100 Standings will also be eligible to race.<br/><br/>
				All athletes have been deemed eligible to race by their National Triathlon Federations
				and are sorted here in T100 Standings order.
			</p>
		)
	}

	// Regular T100 — tentative
	if (isTentativeList && !shouldHideTentative) {
		return (
			<p className="participants-explainer">
				This is not an official start list: these athletes are on the wait list and are the
				likely starters, based on the inclusion criteria of: up to 10 T100 Top-10 athletes;
				those within the top {PTO_TOP_RANK_LIMIT} of the PTO World Rankings*; a minimum
				of 2 discretionary Wildcard athletes.<br/><br/>
				All athletes have been deemed eligible to race by their National Triathlon Federations
				and are sorted here in T100 Standings order, then by PTO World Ranking order based on
				the PTO Rankings Set date detailed above.<br/><br/>
				* The total number of race slots for non T100 Top-10 athletes within the top {PTO_TOP_RANK_LIMIT}
				{' '}of the PTO World Rankings is calculated as 20 minus 2 Wildcards
				minus the number of T100 Top-10 athletes. Any remaining slots after this allocation
				process are discretionary picks.
			</p>
		)
	}

	// Regular T100 — official (with hidden-tentative override for specific race IDs)
	if (shouldHideTentative) {
		return (
			<p className="participants-explainer">
				For the first race of 2026 in each gender, starting slots are offered in this
				order: the top-10 athletes in the 2025 T100 Standings; the top-5 athletes in the
				2025 T100 Contender Rankings; additional Contender Rankings athletes (in order)
				if any T100 Top-10 athletes decline their spots; 2 discretionary Wildcard
				athletes.<br/><br/>
				All athletes have been deemed eligible to race by their National Triathlon Federations
				and are sorted here in T100 Standings order, then by PTO World Ranking order based on
				the PTO Rankings Set date detailed above.<br/><br/>
				* The total number of race slots for non T100 Top-10 athletes within the top {PTO_TOP_RANK_LIMIT}
				{' '}of the PTO World Rankings is calculated as 20 minus 2 Wildcards
				minus the number of T100 Top-10 athletes. Any remaining slots after this allocation
				process are discretionary picks.
			</p>
		)
	}

	// Regular T100 — official (default)
	return (
		<p className="participants-explainer">
			Inclusion criteria for the Start List is: up to 10 T100 Top-10 athletes;
			any athletes ranked within the top {PTO_TOP_RANK_LIMIT} of the PTO World Rankings at
			the point of the initial {cutoffDays} day pre-race cutoff*; 2
			discretionary Wildcard athletes.<br/><br/>
			All athletes have been deemed eligible to race by their National Triathlon Federations
			and are sorted here in T100 Standings order, then by PTO World Ranking order based on
			the PTO Rankings Set date detailed above.<br/><br/>
			* The total number of race slots for non T100 Top-10 athletes within the top {PTO_TOP_RANK_LIMIT}
			{' '}of the PTO World Rankings is calculated as 20 minus 2 Wildcards
			minus the number of T100 Top-10 athletes. Any remaining slots after this allocation
			process are discretionary picks.
		</p>
	)
}

export function dateToString(d)
{
	return moment.utc(d).format("DD MMM")
}

export function dateStatusFormat(date, status) {
	if (!date) {
		return '';
	}

	switch (status) {
		case 'tba':
			return 'TBA';
		case 'month-year':
			return 'TBA ' + moment(date).format("MMMM YYYY")
		case 'year':
			return 'TBA ' + moment(date).format("YYYY")
		default:
			return moment(date).format("D MMMM YYYY")
	}
}

export function getSeconds(t)
{
	if(!t) return -1
	t = t.split(".")[0]
	let parts = t.split(":")
	if(parts.length === 2) return (parseInt(parts[0]) * 60) + parseInt(parts[1])
	else if(parts.length === 3) return (parseInt(parts[0]) * 60 * 60) + (parseInt(parts[1]) * 60) + parseInt(parts[2])

	return -1
}

export function secondsToTime(secs, negok)
{
	//if (secs <= 0) return "--:--";
	let sign = ""
	if(secs < 0 && negok === true)
	{
		sign = "-"
		secs = -secs
	}
	else if (secs <= 0) return "--:--"

	var seconds = secs % 60;
	var minutes = ((secs - seconds) / 60) % 60
	var hours = (secs - seconds - minutes*60) / 3600;
	if (hours > 0)
	{
		return sign + (hours >= 10 ? "" : "") + hours + ":" + (minutes >= 10 ? "" : "0") + minutes + ":" + (seconds >= 10 ? "" : "0") + seconds;
	}
	else if (minutes > 0)
	{
		return sign + (minutes >= 10 ? "" : "") + minutes + ":" + (seconds >= 10 ? "" : "0") + seconds;
	}
	else
	{
		return sign + "00:" + (seconds >= 10 ? "" : "0") + seconds;
	}
}

export function ordinal_suffix_of(i)
{
	var j = i % 10, k = i % 100;
	if (j === 1 && k !== 11) return i + "st";
	if (j === 2 && k !== 12) return i + "nd";
	if (j === 3 && k !== 13) return i + "rd";
	return i + "th";
}

export class AthletePic extends Component {
	render(props)
	{
		if (props.noLink) {
			let pic = props.pic
			return (
				<div class={`athlete-pic-group ${props.class}`}>
					<picture class={'athlete-pic ' + (props.class || "")}>
						{ pic.sources.map(x => (
							<source type={x.type} srcset={x.srcset} data-srcset={x.srcset} sizes={props.sizes} alt={props.link}></source>
						)) }
						<img src={pic.img.src} data-src={pic.img.src}/>
					</picture>
				</div>
			)
		}

		if(props.pic && props.pic !== null && props.pic !== undefined)
		{
			let pic = props.pic
			return (
				<a href={props.link && (props.link[0] === '/' ? props.link : '/athlete/' + props.link)} class={`athlete-pic-group ${props.class}`} target={props.embed ? "_pto" : "" }>
					<picture class={'athlete-pic ' + (props.class || "")}>
						{ pic.sources.map(x => (
							<source type={x.type} srcset={x.srcset} data-srcset={x.srcset} sizes={props.sizes} alt={props.link}></source>
						)) }
						<img src={pic.img.src} data-src={pic.img.src}/>
					</picture>
				</a>
			)
		}

		//console.log(props.nolink != true, props)

		//if(props.src)
		return (
			<a href={props.link && props.link[0] === '/' ? props.link : '/athlete/' + props.link} class='athlete-pic-group'>
				<img class={'no-pic athlete-pic ' + (props.class || "")} src={props.src} sizes={props.sizes} srcset={props.srcset} alt={props.link}/>
				<div class={"nopic"}></div>
			</a>)
	}
}

const smoothing = 0.2

const line = (pointA, pointB) => {
	const lengthX = pointB[0] - pointA[0]
	const lengthY = pointB[1] - pointA[1]
	return {
		length: Math.sqrt(Math.pow(lengthX, 2) + Math.pow(lengthY, 2)),
		angle: Math.atan2(lengthY, lengthX)
	}
}

const controlPoint = (current, previous, next, reverse) => {
	const p = previous || current
	const n = next || current

	const o = line(p, n)

	const angle = o.angle + (reverse ? Math.PI : 0)
	const length = o.length * smoothing

	const x = current[0] + Math.cos(angle) * length
	const y = current[1] + Math.sin(angle) * length
	return [x, y]
}

const bezierCommand = (point, i, a) => {
	const cps = controlPoint(a[i - 1], a[i - 2], point)
	const cpe = controlPoint(point, a[i - 1], a[i + 1], true)
	return `C ${cps[0]},${cps[1]} ${cpe[0]},${cpe[1]} ${point[0]},${point[1]}`
}

const svgPath = (points, command) => {
	const d = points.reduce((acc, point, i, a) => i === 0 ? `M ${point[0]},${point[1]}` : `${acc} ${command(point, i, a)}` , '')
	return d
}

export class GraphRankings extends Component {
	render(props) {
		let rankings = []
		for (let index = props.rankings.length - 1; index >=0; index--) {
			if (props.rankings[index].overall_rank > 10000) {
				continue
			}
			rankings.push(props.rankings[index])
		}

		let swimPoints = [], bikePoints = [], runPoints = [], overallPoints = [], labels = [], labels_y = []

		let max_rank = rankings
			.map(el => Math.max(el.overall_rank, Math.max(el.swim_rank, Math.max(el.bike_rank, el.run_rank))))
			.reduce((m,c) => Math.max(m,c), 0)
		let min_rank = rankings
			.map(el => Math.min(el.overall_rank, Math.min(el.swim_rank, Math.min(el.bike_rank, el.run_rank))))
			.reduce((m,c) => Math.min(m,c), 10000)

		min_rank = Math.max(1, min_rank - 5)
		max_rank = max_rank + 5

		let step = parseInt((max_rank - min_rank) / 4)
		for (let y = min_rank; y < max_rank; y += step) {
			labels_y.push({ name: y, y: 10 + 170 * ((y - min_rank) / (max_rank - min_rank))})
		}

		for (let x = 0; x < rankings.length; x++) {
			let fx = 50 + (620 * x / (rankings.length - 1))
			let fy = (rankings[x].overall_rank - min_rank) / (max_rank - min_rank)
			overallPoints.push([fx, 10 + (170 * fy)])

			fy = (rankings[x].swim_rank - min_rank) / (max_rank - min_rank)
			swimPoints.push([fx, 10 + (170 * fy)])

			fy = (rankings[x].bike_rank - min_rank) / (max_rank - min_rank)
			bikePoints.push([fx, 10 + (170 * fy)])

			fy = (rankings[x].run_rank - min_rank) / (max_rank - min_rank)
			runPoints.push([fx, 10 + (170 * fy)])

			labels.push({name: rankings[x].date.getFullYear(), x: fx, sx: 620 / (rankings.length - 1), data: rankings[x] })
		}

		let show2022Explainer = false
		let show2026Explainer = false

		return (
			<div className='graph-container'>
				<div className='graph-resizer'>
					<svg width="100%" height="100%" viewBox="0 0 700 210" preserveAspectRatio="none" style='user-select: none; overflow: visible'>
						<defs>
							<filter id="shadow">
								<feDropShadow dx="0" dy="0" stdDeviation="4" flood-color="black" flood-opacity="0.5"/>
							</filter>
						</defs>
						<g style='pointer-events: none;'></g>
						<g style='pointer-events: none;' className='grid'>
							{ labels_y.map(p => (<text x="35" y={p.y}>#{p.name}</text>)) }
							{ labels_y.map(p => (<line x1="40" x2="690" y1={p.y} y2={p.y} style='stroke: #888; stroke-width: 0.5px;'/>)) }
							{ labels.map(p => (<line x1={p.x} x2={p.x} y1="5" y2="185" style='stroke: #888; stroke-width: 0.5px;'/>)) }
							<g className='labels'>
								{ labels.map(p => (<text x={p.x} y={200}>{ p.name }{p.name === 2023 && '*'}{p.name === 2026 && '**'}</text>)) }
							</g>
						</g>
						<g className='swim'>
							<path d={ svgPath(swimPoints, bezierCommand) }/>
							{ swimPoints.map(p => (<circle cx={p[0]} cy={p[1]} r="6"/>)) }
						</g>
						<g className='bike'>
							<path d={ svgPath(bikePoints, bezierCommand) }/>
							{ bikePoints.map(p => (<circle cx={p[0]} cy={p[1]} r="6"/>)) }
						</g>
						<g className='run'>
							<path d={ svgPath(runPoints, bezierCommand) }/>
							{ runPoints.map(p => (<circle cx={p[0]} cy={p[1]} r="6"/>)) }
						</g>
						<g className='overall'>
							<path d={ svgPath(overallPoints, bezierCommand) }/>
							{ overallPoints.map(p => (<circle cx={p[0]} cy={p[1]} r="6"/>)) }
						</g>
						<g className='mouse'>
							{ labels.map(p => {
								let width = 180
								let top = -95
								let posx = p.x - width * 0.5
								if (posx < 10) {
									posx = 10
								}
								if (posx + width > 700 - 10) {
									posx = 700 - 10 - width
								}
								if (p.name === 2023) {
									show2022Explainer = true
								}
								if (p.name === 2026) {
									show2026Explainer = true
								}
								return (
									<g className='hover'>
										<rect x={p.x - p.sx * 0.5} width={ p.sx } y={ 0 } height="210" style="fill: transparent;"/>
										<g className='popup' style='pointer-events: none; filter: url(#shadow);'>
											<rect x={posx} y={top - 5} width={width} height={100}/>
											<text className='year' x={posx + width * 0.5 } y={ top + 10 }>{p.name}</text>

											<text className='overall' x={posx + 10} y={ top + 30 }>Overall</text>
											<text className='points' x={ posx + width * 0.5 } y={ top + 30 }>{p.data.overall_points.toFixed(2)} pts</text>
											<text className='rank' x={ posx + width - 10 } y={ top + 30 }>#{p.data.overall_rank}</text>

											<text className='swim' x={posx + 10} y={ top + 55 }>Swim</text>
											<text className='points' x={ posx + width * 0.5 } y={ top + 55 }>{p.data.swim_points.toFixed(2)} pts</text>
											<text className='rank' x={ posx + width - 10 } y={ top + 55 }>#{p.data.swim_rank}</text>

											<text className='bike' x={posx + 10} y={ top + 70 }>Bike</text>
											<text className='points' x={ posx + width * 0.5 } y={ top + 70 }>{p.data.bike_points.toFixed(2)} pts</text>
											<text className='rank' x={ posx + width - 10 } y={ top + 70 }>#{p.data.bike_rank}</text>

											<text className='run' x={posx + 10} y={ top + 85 }>Run</text>
											<text className='points' x={ posx + width * 0.5 } y={ top + 85 }>{p.data.run_points.toFixed(2)} pts</text>
											<text className='rank' x={ posx + width - 10 } y={ top + 85 }>#{p.data.run_rank}</text>
										</g>
									</g>
								)
							}) }
						</g>
					</svg>
				</div>
				{show2022Explainer && <p className='explainer--2022-recalculated mt-3'>* New PTO World Ranking System implemented</p>}
				{show2026Explainer && <p className='explainer--2022-recalculated mt-3'>** 2026 PTO World Rankings calculated on avg of athletes' 4 best races</p>}
			</div>
		)
	}
}

export function numberWithCommas(x) {
	return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const sortParticipants = (participantsList, isWaitingList = false) => {
    return participantsList.sort((a, b) => {

        if (isWaitingList) {
            // Wait list: non-late entries first (by PTO world rank), late entries at the bottom (by late entry date)
            const aIsLateEntry = a.late_entry === 1 || a.is_late_entry === true;
            const bIsLateEntry = b.late_entry === 1 || b.is_late_entry === true;

            if (aIsLateEntry !== bIsLateEntry) {
                return aIsLateEntry ? 1 : -1;
            }

            // If both are late entries, sort by late_entry_at (earliest first)
            if (aIsLateEntry && bIsLateEntry) {
                const aDate = a.late_entry_at ? new Date(a.late_entry_at) : new Date(9999, 11, 31);
                const bDate = b.late_entry_at ? new Date(b.late_entry_at) : new Date(9999, 11, 31);
                return aDate - bDate;
            }

            // Non-late entries: sort by PTO world rank only
            const aPtoRank = (a.pto_rank && a.pto_rank !== 99999) ? a.pto_rank : Number.MAX_SAFE_INTEGER;
            const bPtoRank = (b.pto_rank && b.pto_rank !== 99999) ? b.pto_rank : Number.MAX_SAFE_INTEGER;
            return aPtoRank - bPtoRank;
        }

        // Start list: T100 rank first, then PTO rank
        // Treat 99999 or missing as very high (no rank)
        const aT100Rank = (a.t100_rank && a.t100_rank !== 99999) ? a.t100_rank : Number.MAX_SAFE_INTEGER;
        const bT100Rank = (b.t100_rank && b.t100_rank !== 99999) ? b.t100_rank : Number.MAX_SAFE_INTEGER;

        if (aT100Rank !== bT100Rank) {
            return aT100Rank - bT100Rank;
        }

        // Second priority: PTO rank
        const aPtoRank = (a.pto_rank && a.pto_rank !== 99999) ? a.pto_rank : Number.MAX_SAFE_INTEGER;
        const bPtoRank = (b.pto_rank && b.pto_rank !== 99999) ? b.pto_rank : Number.MAX_SAFE_INTEGER;

        return aPtoRank - bPtoRank;
    });
};

export const calculateListRaceRanks = (participantsList) => {
    if (!participantsList || participantsList.length === 0) {
        return participantsList;
    }

    // Create a copy to avoid mutating the original array
    const participants = [...participantsList];

    // Helper function to get effective T100 rank (treats 99999 as very high number for sorting)
    const getEffectiveT100Rank = (rank) => rank === 99999 ? Number.MAX_SAFE_INTEGER : rank;

    // Calculate swim race ranks
    const swimSorted = [...participants].sort((a, b) =>
        a.swim_rank - b.swim_rank ||
        getEffectiveT100Rank(a.t100_rank) - getEffectiveT100Rank(b.t100_rank) ||
        a.overall_rank - b.overall_rank
    );
    swimSorted.forEach((participant, index) => {
        participant.swim_race_rank = index + 1;
    });

    // Calculate bike race ranks
    const bikeSorted = [...participants].sort((a, b) =>
        a.bike_rank - b.bike_rank ||
        getEffectiveT100Rank(a.t100_rank) - getEffectiveT100Rank(b.t100_rank) ||
        a.overall_rank - b.overall_rank
    );
    bikeSorted.forEach((participant, index) => {
        participant.bike_race_rank = index + 1;
    });

    // Calculate run race ranks
    const runSorted = [...participants].sort((a, b) =>
        a.run_rank - b.run_rank ||
        getEffectiveT100Rank(a.t100_rank) - getEffectiveT100Rank(b.t100_rank) ||
        a.overall_rank - b.overall_rank
    );
    runSorted.forEach((participant, index) => {
        participant.run_race_rank = index + 1;
    });

    return participants;
};
